import { useState, useRef, useCallback } from "react";
import { getBackendUrl } from "../utils/backendUrl";

interface VoiceRecordingState {
  isRecording: boolean;
  isUploading: boolean;
  error: string | null;
  volume: number;
  waveformBars: number[];
}

interface TranscriptionResponse {
  text: string;
  language: string;
  duration?: number;
  status: string;
}

interface TranscriptionError {
  error: string;
  code: string;
}

const MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

const MAX_RECORDING_TIME_MS = 300000;
const VOLUME_THRESHOLD = 20;
const VOLUME_UPDATE_INTERVAL = 50;
const WAVEFORM_BAR_COUNT = 12;
const VOICE_FREQ_BINS = 24;

export const useVoiceRecording = () => {
  const [state, setState] = useState<VoiceRecordingState>({
    isRecording: false,
    isUploading: false,
    error: null,
    volume: 0,
    waveformBars: [],
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const volumeIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const getSupportedMimeType = useCallback((): string | null => {
    for (const mimeType of MIME_TYPES) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        return mimeType;
      }
    }
    return null;
  }, []);

  const isRecordingSupported = useCallback((): boolean => {
    return !!(
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function" &&
      window.MediaRecorder &&
      getSupportedMimeType()
    );
  }, [getSupportedMimeType]);

  const cleanupMediaResources = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;

    if (volumeIntervalRef.current) {
      clearInterval(volumeIntervalRef.current);
      volumeIntervalRef.current = null;
    }

    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
      recordingTimeoutRef.current = null;
    }
  }, []);

  const updateVolume = useCallback(() => {
    if (!analyserRef.current) {
      return;
    }

    const analyser = analyserRef.current;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(dataArray);

    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i];
    }
    const average = sum / dataArray.length;
    const volumeLevel = Math.min(100, (average / 128) * 100);

    // Build symmetric waveform: lowest freqs (most voice energy) in the center
    const halfCount = WAVEFORM_BAR_COUNT / 2;
    const binsToUse = Math.min(VOICE_FREQ_BINS, dataArray.length);
    const binsPerBar = Math.floor(binsToUse / halfCount);

    const halfValues: number[] = [];
    for (let b = 0; b < halfCount; b++) {
      let barSum = 0;
      for (let j = 0; j < binsPerBar; j++) {
        barSum += dataArray[b * binsPerBar + j];
      }
      const raw = barSum / binsPerBar / 255;
      halfValues.push(Math.min(100, Math.pow(raw, 0.65) * 80));
    }

    const jitter = () => (Math.random() - 0.5) * 5;
    const reversed = halfValues.slice().reverse();
    const bars: number[] = [];
    for (let i = 0; i < halfCount; i++) {
      bars.push(Math.max(5, Math.min(100, reversed[i] + jitter())));
    }
    for (let i = 0; i < halfCount; i++) {
      bars.push(Math.max(5, Math.min(100, halfValues[i] + jitter())));
    }

    setState((prev) => ({
      ...prev,
      volume: volumeLevel,
      waveformBars: bars,
    }));
  }, []);

  const startRecording = useCallback(async (): Promise<void> => {
    try {
      if (!isRecordingSupported()) {
        throw new Error(
          "Your browser does not support audio recording. Please use Chrome, Firefox, or Edge.",
        );
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.15;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      volumeIntervalRef.current = setInterval(
        updateVolume,
        VOLUME_UPDATE_INTERVAL,
      );

      const mimeType = getSupportedMimeType();
      if (!mimeType) {
        throw new Error("No supported audio format found");
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error("MediaRecorder error:", event);
        cleanupMediaResources();
        setState((prev) => ({
          ...prev,
          isRecording: false,
          error: "Recording error occurred",
          volume: 0,
          waveformBars: [],
        }));
      };

      mediaRecorder.start(1000);
      mediaRecorderRef.current = mediaRecorder;

      recordingTimeoutRef.current = setTimeout(() => {
        if (mediaRecorderRef.current?.state === "recording") {
          stopRecording();
        }
      }, MAX_RECORDING_TIME_MS);

      setState({
        isRecording: true,
        isUploading: false,
        error: null,
        volume: 0,
        waveformBars: [],
      });
    } catch (error) {
      console.error("Failed to start recording:", error);
      cleanupMediaResources();

      let errorMessage = "Failed to start recording";
      if (error instanceof Error) {
        if (error.name === "NotAllowedError") {
          errorMessage =
            "Microphone access denied. Please allow microphone access in your browser settings.";
        } else if (error.name === "NotFoundError") {
          errorMessage = "No microphone found. Please connect a microphone.";
        } else {
          errorMessage = error.message;
        }
      }

      setState({
        isRecording: false,
        isUploading: false,
        error: errorMessage,
        volume: 0,
        waveformBars: [],
      });

      throw new Error(errorMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getSupportedMimeType,
    isRecordingSupported,
    updateVolume,
    cleanupMediaResources,
  ]);

  const stopRecording = useCallback(async (): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const mediaRecorder = mediaRecorderRef.current;

      if (!mediaRecorder) {
        reject(new Error("No active recording"));
        return;
      }

      if (mediaRecorder.state === "inactive") {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: mediaRecorder.mimeType,
        });
        cleanupMediaResources();
        resolve(audioBlob);
        return;
      }

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: mediaRecorder.mimeType,
        });
        cleanupMediaResources();
        setState((prev) => ({
          ...prev,
          isRecording: false,
          volume: 0,
          waveformBars: [],
        }));
        resolve(audioBlob);
      };

      mediaRecorder.stop();
    });
  }, [cleanupMediaResources]);

  const transcribeAudio = useCallback(
    async (
      audioBlob: Blob,
      language: string = "auto",
      postProcess: boolean = false,
    ): Promise<string> => {
      try {
        setState((prev) => ({ ...prev, isUploading: true, error: null }));

        const formData = new FormData();
        const extension = audioBlob.type.includes("webm") ? "webm" : "mp3";
        const audioFile = new File([audioBlob], `recording.${extension}`, {
          type: audioBlob.type,
        });

        formData.append("file", audioFile);
        formData.append("language", language);
        formData.append("post_process", postProcess ? "true" : "false");

        const response = await fetch(
          `${getBackendUrl()}/v1/ai/speech-to-text/transcribe`,
          {
            method: "POST",
            body: formData,
          },
        );

        if (!response.ok) {
          const errorData: TranscriptionError = await response.json();
          throw new Error(errorData.error || "Transcription failed");
        }

        const result: TranscriptionResponse = await response.json();
        setState((prev) => ({ ...prev, isUploading: false }));
        return result.text;
      } catch (error) {
        console.error("Transcription error:", error);
        let errorMessage = "Failed to transcribe audio";
        if (error instanceof Error) {
          errorMessage = error.message;
        }
        setState((prev) => ({
          ...prev,
          isUploading: false,
          error: errorMessage,
        }));
        throw new Error(errorMessage);
      }
    },
    [],
  );

  const cancelRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current;
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.onstop = null;
      mediaRecorder.stop();
      audioChunksRef.current = [];
    }
    cleanupMediaResources();
    setState({
      isRecording: false,
      isUploading: false,
      error: null,
      volume: 0,
      waveformBars: [],
    });
  }, [cleanupMediaResources]);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    isRecording: state.isRecording,
    isUploading: state.isUploading,
    error: state.error,
    volume: state.volume,
    volumeThreshold: VOLUME_THRESHOLD,
    waveformBars: state.waveformBars,
    isRecordingSupported: isRecordingSupported(),
    startRecording,
    stopRecording,
    transcribeAudio,
    cancelRecording,
    clearError,
  };
};
